import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin, Globe, User, GraduationCap, Briefcase, Users, Heart, Home, BookOpen, Camera, Gamepad } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const TealProfessionalTemplate: React.FC<TemplateProps> = ({ data, isContinuationPage = false, showPlaceholders = false, pageIndex = 0, fullData }) => {
    const primaryColor = data.style?.primaryColor || '#004E6E';
    const { fontFamily = 'font-sans', textColor = '#1e293b', backgroundColor = '#ffffff', sectionBackgroundColor = primaryColor, imageBorderColor = 'rgba(255,255,255,0.3)' } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '00 123 456 789 01' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'yourmail@gmail.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? '45 Street City/Country' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet colore magna aliquam erat volutpat. Ut wisi enim ad minim veniam, quis nostrud exerci tation ullamcorper suscipit lobortis nisl ut aliquip ex ea commodo consequat. Duis autem vel eum iriure dolor in hendrerit in vulputate velit esse molestie consequat, vel illum dolore eu fugiat nulla facilisis.'
        : '');
    const photoSrc = data.personalInfo.profileImage || (showPlaceholders ? '' : '');

    return (
        <div className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex overflow-hidden ${fontFamily}`} style={{ backgroundColor: backgroundColor, color: textColor }}>

            {/* Gray Header */}
            <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-r from-slate-400 to-slate-500"></div>

            {/* Left Sidebar - Teal */}
            <div data-cv-sidebar className="w-[38%] text-white p-10 pt-24 relative z-10 overflow-hidden" style={{ backgroundColor: sectionBackgroundColor }}>
                {isContinuationPage ? (
                    <div className="mt-4">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest text-white/80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <>
                {/* Profile Photo */}
                {(photoSrc || showPlaceholders) && (
                <div className="mb-5 flex justify-center">
                    <div className="w-44 h-44 rounded-full border-8 overflow-hidden shadow-2xl bg-slate-300" style={{ borderColor: imageBorderColor }}>
                        {photoSrc ? (
                            <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-slate-300"></div>
                        )}
                    </div>
                </div>
                )}

                {/* Contact Section */}
                {(phone || email || address || showPlaceholders) && (
                <section className="mb-5">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[12px] border-l-white border-b-[8px] border-b-transparent"></div>
                        <h2 className="text-xl font-black uppercase tracking-wider">{CV_LABELS.contact}</h2>
                    </div>
                    <div className="cv-fs-contact space-y-4 pl-4">
                        {email && (
                        <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5" />
                            <span className="text-sm break-all">{email}</span>
                        </div>
                        )}
                        {phone && (
                        <div className="flex items-center gap-3">
                            <Phone className="w-5 h-5" />
                            <span className="text-sm">{phone}</span>
                        </div>
                        )}
                        {address && (
                        <div className="flex items-center gap-3">
                            <MapPin className="w-5 h-5" />
                            <span className="text-sm">{address}</span>
                        </div>
                        )}
                    </div>
                </section>
                )}

                {/* Language Section */}
                <section className="mb-5">
                    <SectionHeading title={CV_LABELS.languages} isContinuation={langContinued} primaryColor="#ffffff" variant="bar" />
                    <div className="cv-fs-languages space-y-4 pl-4">
                        {(data.languages && data.languages.length > 0 ? data.languages.slice(0, 5) : showPlaceholders ? [
                            { name: 'French', level: 95 },
                            { name: 'Chinese', level: 85 },
                            { name: 'Spanish', level: 75 },
                            { name: 'Arabic', level: 90 },
                            { name: 'English', level: 100 },
                            { name: 'Bangla', level: 80 },
                            { name: 'Hindi', level: 88 }
                        ] : []).map((lang, i) => (
                            <div
                                key={lang.id || lang.name || i}
                                data-cv-block
                                data-cv-item-id={lang.id || undefined}
                            >
                                <p className="text-sm font-bold mb-2">{lang.name}</p>
                                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: `${primaryColor}40` }}>
                                    <div
                                        className="h-full bg-white rounded-full"
                                        style={{ width: `${lang.level}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Reference Section */}
                <section className="mb-5">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[12px] border-l-white border-b-[8px] border-b-transparent"></div>
                        <h2 className="text-xl font-black uppercase tracking-wider">{CV_LABELS.references}</h2>
                    </div>
                    {data.references && data.references.length > 0 ? data.references.slice(0, 1).map((ref, i) => (
                        <div
                            key={ref.id || i}
                            className="cv-fs-references pl-4"
                            data-cv-block
                            data-cv-item-id={ref.id || undefined}
                        >
                            <p className="text-base font-black mb-1">{ref.name}</p>
                            <p className="text-xs italic mb-3">{ref.position}</p>
                            <p className="text-xs mb-1"><span className="font-bold">Company:</span> {ref.company}</p>
                            <p className="text-xs mb-1"><span className="font-bold">Mail:</span> {ref.email}</p>
                            <p className="text-xs mb-1"><span className="font-bold">Phone:</span> {ref.phone}</p>
                        </div>
                    )) : showPlaceholders ? (
                        <div className="cv-fs-references pl-4">
                            <p className="text-base font-black mb-1">Nom de la référence</p>
                            <p className="text-xs italic mb-3">Poste</p>
                            <p className="text-xs mb-1"><span className="font-bold">Company:</span> Entreprise</p>
                            <p className="text-xs mb-1"><span className="font-bold">Mail:</span> demo@gmail.com</p>
                            <p className="text-xs mb-1"><span className="font-bold">Phone:</span> 00 123 456 789</p>
                            <p className="text-xs"><span className="font-bold">Address:</span> Street name, Country</p>
                        </div>
                    ) : null}
                </section>

                {/* Hobbies Section */}
                {showPlaceholders && (
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[12px] border-l-white border-b-[8px] border-b-transparent"></div>
                        <h2 className="text-xl font-black uppercase tracking-wider">{CV_LABELS.interests}</h2>
                    </div>
                    <div className="cv-fs-interests grid grid-cols-4 gap-4 pl-4">
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                                <Home className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                                <BookOpen className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                                <Camera className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                                <Gamepad className="w-6 h-6" />
                            </div>
                        </div>
                    </div>
                </section>
                )}
                    </>
                )}
            </div>

            {/* Right Content - White */}
            <div data-cv-main className="flex-1 min-h-0 overflow-hidden p-12 pt-28" style={{ backgroundColor: backgroundColor, color: textColor }}>
                {isContinuationPage ? (
                    <div className="mb-6 pb-3 border-b border-slate-200">
                        <p className="text-sm font-bold uppercase tracking-widest" style={{ color: textColor, opacity: 0.5 }}>{CV_LABELS.suite}</p>
                    </div>
                ) : (
                <header className="mb-6">
                    <h1 className="cv-fs-name text-6xl font-black uppercase leading-none mb-2">
                        <span style={{ color: primaryColor }}>{data.personalInfo.firstName || (showPlaceholders ? 'JOHN' : '')}</span>{' '}
                        <span className="text-slate-900" style={{ color: '#' === textColor[0] ? textColor : '#1e293b' }}>{data.personalInfo.lastName || (showPlaceholders ? 'SMITH' : '')}</span>
                    </h1>
                    <p className="cv-fs-profession text-sm font-bold uppercase tracking-[0.3em]" style={{ color: textColor, opacity: 0.7 }}>

                        {data.personalInfo.profession || (showPlaceholders ? 'PROFESSIONAL TITLE' : '')}
                    </p>
                </header>
                )}


                {isContinuationPage && data.skills && data.skills.length > 0 && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-skills grid grid-cols-3 gap-4">
                            {data.skills.map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center gap-2 min-w-0"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[8px] font-bold shrink-0" style={{ borderColor: primaryColor, color: primaryColor }}>
                                        {Math.round(skill.level || 50)}%
                                    </div>
                                    <span className="text-xs font-bold break-words" style={{ color: textColor }}>{skill.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Profile Section */}
                {summary && (
                <section className="mb-5">
                    <div className="flex items-center gap-3 mb-4">
                        <User className="w-6 h-6" style={{ color: primaryColor }} />
                        <h2 className="text-2xl font-black uppercase" style={{ color: primaryColor }}>{CV_LABELS.profile}</h2>
                    </div>
                    <p className="cv-fs-summary text-xs leading-relaxed pl-9" style={{ color: textColor, opacity: 0.8 }}>
                        {summary}
                    </p>
                </section>
                )}

                {/* Education Section */}
                <section className="mb-5">
                    <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="bar" />
                    <div className="cv-fs-education space-y-6 pl-9">
                        {data.education.length > 0 ? data.education.map((edu, i) => (
                            <div
                                key={edu.id || i}
                                className="relative pl-6 border-l-2"
                                style={{ borderColor: primaryColor }}
                                data-cv-block
                                data-cv-item-id={edu.id || undefined}
                            >
                                <div className="absolute -left-2 top-1 w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: primaryColor }}></div>
                                <h3 className="text-sm font-black uppercase mb-1" style={{ color: textColor }}>{edu.institution}</h3>

                                <p className="text-xs font-bold mb-2" style={{ color: primaryColor }}>{edu.degree} ({edu.startDate.slice(0, 4)})</p>
                                {edu.description && (
                                    <p className="text-xs leading-relaxed" style={{ color: textColor, opacity: 0.7 }}>{edu.description}</p>
                                )}
                            </div>
                        )) : showPlaceholders ? (
                            <>
                                <div className="relative pl-6 border-l-2" style={{ borderColor: primaryColor }}>
                                    <div className="absolute -left-2 top-1 w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: primaryColor }}></div>
                                    <h3 className="text-sm font-black uppercase mb-1" style={{ color: textColor }}>Établissement</h3>

                                    <p className="text-xs font-bold mb-2" style={{ color: primaryColor }}>Complete (Exam Name) (2013)</p>
                                </div>
                                <div className="relative pl-6 border-l-2" style={{ borderColor: primaryColor }}>
                                    <div className="absolute -left-2 top-1 w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: primaryColor }}></div>
                                    <h3 className="text-sm font-black uppercase mb-1" style={{ color: textColor }}>Établissement</h3>

                                    <p className="text-xs font-bold mb-2" style={{ color: primaryColor }}>Complete (Exam Name) (2013)</p>
                                </div>
                            </>
                        ) : null}
                    </div>
                </section>

                {/* Experience Section */}
                <section className="mb-5">
                    <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="bar" />
                    <div className="cv-fs-experience space-y-6 pl-9">
                        {data.experience.length > 0 ? data.experience.map((exp, i) => (
                            <div
                                key={exp.id || i}
                                className="relative pl-6 border-l-2"
                                style={{ borderColor: primaryColor }}
                                data-cv-block
                                data-cv-item-id={exp.id || undefined}
                            >
                                <div className="absolute -left-2 top-1 w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: primaryColor }}></div>
                                <h3 className="text-sm font-black uppercase mb-1" style={{ color: textColor }}>{exp.company}</h3>

                                <p className="text-xs font-bold mb-2" style={{ color: primaryColor }}>({exp.startDate}-{frPresent(exp.isCurrent, exp.endDate)})</p>
                                <p className="text-xs leading-relaxed" style={{ color: textColor, opacity: 0.7 }}>{exp.position}, {exp.description}</p>

                            </div>
                        )) : showPlaceholders ? (
                            <>
                                <div className="relative pl-6 border-l-2" style={{ borderColor: primaryColor }}>
                                    <div className="absolute -left-2 top-1 w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: primaryColor }}></div>
                                    <h3 className="text-sm font-black text-slate-900 uppercase mb-1">ENTREPRISE</h3>
                                    <p className="text-xs font-bold mb-2" style={{ color: primaryColor }}>(2011-2013)</p>
                                    <p className="text-xs leading-relaxed" style={{ color: textColor, opacity: 0.7 }}>New York, 1,2,3, Street Country</p>

                                </div>
                            </>
                        ) : null}
                    </div>
                </section>

                {/* Skills Section */}
                {!isContinuationPage && (
                <section>
                    <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="bar" />
                    <div className="cv-fs-skills grid grid-cols-3 gap-3 pl-9">
                        {(data.skills && data.skills.length > 0 ? data.skills : showPlaceholders ? [
                            { name: 'Management' }, { name: 'Management' },
                            { name: 'Teamwork' }, { name: 'Teamwork' },
                            { name: 'Handling Customer' }, { name: 'Handling Customer' },
                            { name: 'Handling Customer' }, { name: 'Handling Customer' }
                        ] : []).map((skill, i) => (
                            <div
                                key={'id' in skill ? skill.id || i : i}
                                className="flex items-center gap-2"
                                data-cv-block
                                data-cv-item-id={'id' in skill ? skill.id || undefined : undefined}
                            >
                                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: primaryColor }}></div>
                                <span className="text-xs font-bold uppercase" style={{ color: textColor }}>{skill.name}</span>

                            </div>
                        ))}
                    </div>
                </section>
                )}
            </div>
        </div>
    );
};

export default TealProfessionalTemplate;
